import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../interfaces/api';
import {
  CreatePinnedQueryPayload,
  PinnedQuery,
} from '../interfaces/pinned-query';
import { ChatService } from './chat.service';

@Injectable({
  providedIn: 'root',
})
export class PinnedQueryService {
  private readonly http = inject(HttpClient);
  private readonly chatService = inject(ChatService);

  readonly pinnedQueries = signal<PinnedQuery[]>([]);
  readonly isLoading = signal(false);
  readonly isMutating = signal(false);

  /**
   * Pending pinned-query send. BehaviorSubject so a late composer subscribe
   * still receives the click (plain Subject would drop it).
   */
  private readonly pendingSendSubject = new BehaviorSubject<string | null>(null);
  readonly sendQueryRequest$: Observable<string> = this.pendingSendSubject.pipe(
    filter((text): text is string => !!text),
  );

  loadPinnedQueries(): void {
    const schema = this.chatService.schema;
    if (!schema) {
      return;
    }

    this.isLoading.set(true);
    this.fetchPinnedQueries()
      .pipe(
        catchError(() => of([] as PinnedQuery[])),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: (items) => this.setPinned(items),
      });
  }

  pinQuery(payload: CreatePinnedQueryPayload): Observable<PinnedQuery> {
    const text = payload?.text?.trim();
    if (!text) {
      return throwError(() => new Error('text is required to pin a query'));
    }

    this.isMutating.set(true);
    return this.createPinnedQuery({ ...payload, text }).pipe(
      tap((created) => {
        this.setPinned([created, ...this.pinnedQueries().filter((p) => p.id !== created.id)]);
      }),
      catchError((err) => throwError(() => err)),
      finalize(() => this.isMutating.set(false)),
    );
  }

  unpinQuery(id: string): Observable<{ success: boolean }> {
    if (!id?.trim()) {
      return throwError(() => new Error('id is required to unpin a query'));
    }

    this.isMutating.set(true);
    return this.deletePinnedQuery(id).pipe(
      tap(() => {
        this.setPinned(this.pinnedQueries().filter((p) => p.id !== id));
      }),
      catchError((err) => throwError(() => err)),
      finalize(() => this.isMutating.set(false)),
    );
  }

  findPinnedForMessage(messageId?: string, text?: string): PinnedQuery | undefined {
    const pins = this.pinnedQueries();
    if (messageId) {
      const byMessage = pins.find((p) => p.message_id === messageId);
      if (byMessage) {
        return byMessage;
      }
    }
    if (text) {
      const trimmed = text.trim();
      return pins.find((p) => p.text.trim() === trimmed);
    }
    return undefined;
  }

  isPinned(messageId?: string, text?: string): boolean {
    return !!this.findPinnedForMessage(messageId, text);
  }

  requestSend(text: string): void {
    const trimmed = text?.trim();
    if (!trimmed) {
      return;
    }
    this.pendingSendSubject.next(trimmed);
  }

  /** Clear after the composer consumes the pending send (avoids remount re-send). */
  clearPendingSend(): void {
    if (this.pendingSendSubject.value != null) {
      this.pendingSendSubject.next(null);
    }
  }

  private fetchPinnedQueries(): Observable<PinnedQuery[]> {
    return this.chatService.withAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.get<ApiResponse<PinnedQuery[]>>(
          `${environment.baseURL}/v1/pinned-query`,
          { headers },
        ),
      ),
      map((res) => res.data || []),
    );
  }

  private createPinnedQuery(
    body: CreatePinnedQueryPayload,
  ): Observable<PinnedQuery> {
    return this.chatService.withAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.post<ApiResponse<PinnedQuery>>(
          `${environment.baseURL}/v1/pinned-query`,
          body,
          { headers },
        ),
      ),
      map((res) => res.data),
    );
  }

  private deletePinnedQuery(id: string): Observable<{ success: boolean }> {
    return this.chatService.withAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.delete<ApiResponse<{ success: boolean }>>(
          `${environment.baseURL}/v1/pinned-query/${id}`,
          { headers },
        ),
      ),
      map((res) => res.data),
    );
  }

  private setPinned(items: PinnedQuery[]): void {
    this.pinnedQueries.set(items);
  }
}
